import { useEffect } from "react"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { useAtomValue } from "jotai"
import { selectedFolderIdAtom, mediaSpaceViewModeAtom, EMediaSpaceViewMode } from "~/context/mediaSpaceAtoms"
import { AssetGrid } from "~/components/MediaSpace/AssetGrid"
import { BoardTemplates } from "~/components/MediaSpace/BoardTemplates"
import { api } from "~/utils/api"

function MediaSpaceIndex() {
  const navigate = useNavigate()
  const selectedFolderId = useAtomValue(selectedFolderIdAtom)
  const viewMode = useAtomValue(mediaSpaceViewModeAtom)

  // Auto-detect folder board when in Kanban mode
  const { data: folderBoards } = api.mediaSpace.getBoardsForFolder.useQuery(
    { albumId: selectedFolderId! },
    { enabled: viewMode === EMediaSpaceViewMode.KANBAN && selectedFolderId !== null },
  )

  const { data: allBoards } = api.mediaSpace.getBoards.useQuery(undefined, {
    enabled: viewMode === EMediaSpaceViewMode.KANBAN && selectedFolderId === null,
  })

  // Auto-navigate to folder's board if one exists
  useEffect(() => {
    if (viewMode === EMediaSpaceViewMode.KANBAN && folderBoards && folderBoards.length > 0) {
      void navigate({ to: "/media-space/board/$boardId", params: { boardId: folderBoards[0].id } })
    }
  }, [viewMode, folderBoards, navigate])

  // Get folder name for template display
  const { data: folderTree } = api.mediaSpace.getFolderTree.useQuery()
  const currentFolder = selectedFolderId
    ? (folderTree as { id: string; name: string }[])?.find((f) => f.id === selectedFolderId)
    : null

  if (viewMode === EMediaSpaceViewMode.KANBAN) {
    // If folder has boards, the useEffect above will navigate away
    // If no boards exist, show template picker
    if (selectedFolderId && (!folderBoards || folderBoards.length === 0)) {
      return <BoardTemplates folderId={selectedFolderId} folderName={currentFolder?.name} />
    }

    // No folder selected — show all boards or template picker
    if (!selectedFolderId) {
      if (allBoards && allBoards.length > 0) {
        // Navigate to first board
        void navigate({ to: "/media-space/board/$boardId", params: { boardId: allBoards[0].id } })
        return null
      }
      return <BoardTemplates folderId={null} />
    }

    return null // Loading or navigating
  }

  return (
    <AssetGrid
      folderId={selectedFolderId}
      onUploadClick={() => {
        /* no-op */
      }}
    />
  )
}

export const Route = createFileRoute("/media-space/")({
  component: MediaSpaceIndex,
})
